/**
 * API client for the per-stage thumbs up/down rating feature.
 *
 * Stage identity: `stage_id = `${workflow_type}:${backend_stage_number}``.
 * Most backend stage numbers are derived from the UI's 0-based stage index plus
 * a per-workflow base offset. Workflows with non-linear stage codes use their
 * canonical order descriptors instead. `WORKFLOW_STAGE_BASE` /
 * `backendStageNumber` otherwise remain the inverse of
 * `FlowTable.tsx::stagePosition` (see
 * `issues/20260616-stage-rating-thumbs_implplan.md` §1).
 */

import { apiCall } from './apiClient';
import { USER_FACING_ERROR_MESSAGES } from '../utils/userFacingError';
import { WorkflowType } from '../components/WorkflowTypeGrid';
import {
  CONSTRUCTION_SOW_GENERATION_STAGE_ORDER,
  ONE_TIME_CONSTRUCTION_PRICE_ESTIMATOR_STAGE_ORDER,
} from '../constants/oneTimeConstructionStages';
import { MARKET_RESEARCH_STAGE_ORDER } from '../constants/marketResearchStages';
import { SERVICES_PWS_GENERATION_STAGE_ORDER } from '../constants/servicesPwsGenerationStages';

export type RatingValue = 'up' | 'down';

/**
 * Per-workflow base backend stage number for workflows with linear numbering.
 * Non-linear workflows are handled explicitly by `backendStageNumber`.
 */
export const WORKFLOW_STAGE_BASE: Record<WorkflowType, number> = {
  market_research: 1,
  clause_logic: 101,
  sow_verification: 200,
  construction_sow_generation: 204,
  services_pws_generation: 620,
  wage_determination: 301,
  pnm: 401,
  fopr: 500,
  services: 600,
  // One-time Construction starts in the construction-verification band. Its
  // downstream 604–606 pages use a discontinuous UI sequence, so App passes
  // the backend stage explicitly when it needs a rating identity.
  one_time_construction: 200,
  services_combo: 700,
  gpc: 800,
  // Complex Services reuses the Services authoring band (600–606) and adds
  // 607–616; the master order (not a linear index+base) governs its stepper, so
  // this base is only used by the shared rating-id helper. 600 keeps the reused
  // authoring stages aligned with `services`.
  complex_services: 600,
  other_transactions: 1100,
  post_award_actions: 1000,
  dcma_pbp: 900,
  dcma_crr: 910,
};

/**
 * Backend stage number for a (workflow, 0-based UI stage index) pair.
 */
export const backendStageNumber = (wt: WorkflowType, uiIndex: number): number => {
  if (wt === 'market_research') {
    return MARKET_RESEARCH_STAGE_ORDER[uiIndex] ?? MARKET_RESEARCH_STAGE_ORDER[0];
  }
  if (wt === 'one_time_construction') {
    return ONE_TIME_CONSTRUCTION_PRICE_ESTIMATOR_STAGE_ORDER[uiIndex]
      ?? ONE_TIME_CONSTRUCTION_PRICE_ESTIMATOR_STAGE_ORDER[0];
  }
  if (wt === 'construction_sow_generation') {
    return CONSTRUCTION_SOW_GENERATION_STAGE_ORDER[uiIndex]
      ?? CONSTRUCTION_SOW_GENERATION_STAGE_ORDER[0];
  }
  if (wt === 'services_pws_generation') {
    return SERVICES_PWS_GENERATION_STAGE_ORDER[uiIndex]
      ?? SERVICES_PWS_GENERATION_STAGE_ORDER[0];
  }
  return WORKFLOW_STAGE_BASE[wt] + uiIndex;
};

/**
 * Stable stage identity string used as the per-user rating key.
 */
export const stageRatingId = (wt: WorkflowType, uiIndex: number): string =>
  `${wt}:${backendStageNumber(wt, uiIndex)}`;

/**
 * Persist (or clear) a rating for a stage. `value:"cleared"` removes the rating.
 * Returns the new current value (`null` when cleared / absent).
 */
export async function submitStageRating(args: {
  workflowType: WorkflowType;
  stage: number;
  stageLabel: string;
  value: 'up' | 'down' | 'cleared';
  flowId?: string | null;
}): Promise<{ value: 'up' | 'down' | null }> {
  const response = await apiCall<any>(
    '/api/stage-rating',
    'POST',
    {
      workflow_type: args.workflowType,
      stage: args.stage,
      stage_label: args.stageLabel,
      value: args.value,
      flow_id: args.flowId ?? null,
    },
  );

  if (!response.success) {
    const err = new Error(
      response.error ?? USER_FACING_ERROR_MESSAGES.maintainers_alerted,
    ) as Error & { error_code?: string; technical_detail?: string };
    err.error_code = response.error_code;
    err.technical_detail = response.technical_detail;
    throw err;
  }

  const value = (response as any).current?.value ?? null;
  return { value };
}

/**
 * Fetch all ratings for the current user, keyed by `stage_id`.
 */
export async function fetchStageRatings(): Promise<
  Record<string, { value: RatingValue; stageLabel?: string; updatedAt?: string }>
> {
  const response = await apiCall<{
    ratings?: Record<string, { value: RatingValue; stageLabel?: string; updatedAt?: string }>;
  }>('/api/stage-ratings', 'GET');

  if (!response.success) {
    const err = new Error(
      response.error ?? USER_FACING_ERROR_MESSAGES.maintainers_alerted,
    ) as Error & { error_code?: string; technical_detail?: string };
    err.error_code = response.error_code;
    err.technical_detail = response.technical_detail;
    throw err;
  }

  return (response as any).ratings ?? response.data?.ratings ?? {};
}

/**
 * Fetch the current rating for a single stage id, or `null` when unset.
 */
export async function fetchStageRating(stageId: string): Promise<RatingValue | null> {
  const response = await apiCall<{ value?: RatingValue | null }>(
    `/api/stage-rating?stage_id=${encodeURIComponent(stageId)}`,
    'GET',
  );

  if (!response.success) {
    const err = new Error(
      response.error ?? USER_FACING_ERROR_MESSAGES.maintainers_alerted,
    ) as Error & { error_code?: string; technical_detail?: string };
    err.error_code = response.error_code;
    err.technical_detail = response.technical_detail;
    throw err;
  }

  const value = (response as any).value ?? response.data?.value ?? null;
  return value ?? null;
}
